import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, NotebookPen, Languages, Sparkles, type LucideIcon } from "lucide-react";
import { gsap, useGSAP, SplitText, MM_CONDITIONS } from "@/lib/gsap";
import { ACCENT, type Accent } from "@/components/rebrand";

const FLIP: { key: "notes" | "translator" | "agent"; icon: LucideIcon; accent: Accent }[] = [
  { key: "notes", icon: NotebookPen, accent: "emerald" },
  { key: "translator", icon: Languages, accent: "cyan" },
  { key: "agent", icon: Sparkles, accent: "purple" },
];

/* Aurora glow colors, synced to the active pillar */
const GLOW: Record<Accent, string> = {
  emerald: "rgba(16,185,129,0.20)",
  cyan: "rgba(6,182,212,0.20)",
  purple: "rgba(168,85,247,0.20)",
};
const GLOW_SOFT: Record<Accent, string> = {
  emerald: "rgba(16,185,129,0.10)",
  cyan: "rgba(6,182,212,0.10)",
  purple: "rgba(168,85,247,0.10)",
};

const HOLD = 2.6; // seconds each pillar stays on screen

// Hidden state for cycling words/subs. 0.001 opacity instead of autoAlpha
// (visibility:hidden): browsers only emit an LCP candidate when an element
// first PAINTS, so keeping all pillars technically painted from mount stops
// every flip from registering a new, later LCP (was pushing lab LCP to ~9s).
const HIDDEN = 0.001;

export function StoryHero() {
  const { t } = useTranslation();
  const scope = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const words = gsap.utils.toArray<HTMLElement>(".story-flip-word");
      const subs = gsap.utils.toArray<HTMLElement>(".story-flip-sub");
      const cue = scope.current?.querySelector(".story-scroll-cue") ?? null;

      const buildLoop = (flip: boolean) => {
        gsap.set(words.slice(1), { opacity: HIDDEN });
        gsap.set(subs.slice(1), { opacity: HIDDEN });
        gsap.set(".story-hero-glow", { backgroundColor: GLOW[FLIP[0].accent] });
        gsap.set(".story-hero-glow-soft", { backgroundColor: GLOW_SOFT[FLIP[0].accent] });
        // Every out-transition sits HOLD after the previous in-transition — including
        // the first word on each repeat, so "Live Notes" holds like the others.
        const tl = gsap.timeline({ repeat: -1, delay: 0.6 });
        words.forEach((word, i) => {
          const next = (i + 1) % words.length;
          const outVars = flip ? { opacity: HIDDEN, yPercent: -50, rotationX: 65 } : { opacity: HIDDEN };
          const fromVars = flip ? { opacity: HIDDEN, yPercent: 50, rotationX: -65 } : { opacity: HIDDEN };
          const toVars = flip ? { opacity: 1, yPercent: 0, rotationX: 0 } : { opacity: 1 };
          tl.to(word, { ...outVars, duration: 0.55, ease: "power2.in" }, `+=${HOLD}`)
            .to(subs[i], { opacity: HIDDEN, duration: 0.35 }, "<")
            .fromTo(
              words[next],
              fromVars,
              // immediateRender:false — otherwise the last iteration's fromTo(words[0])
              // applies its hidden "from" state at build time and Live Notes never shows
              { ...toVars, duration: 0.55, ease: "power2.out", immediateRender: false },
              "<0.25",
            )
            .to(subs[next], { opacity: 1, duration: 0.45 }, "<0.15")
            // aurora follows the active pillar
            .to(".story-hero-glow", { backgroundColor: GLOW[FLIP[next].accent], duration: 0.9 }, "<")
            .to(".story-hero-glow-soft", { backgroundColor: GLOW_SOFT[FLIP[next].accent], duration: 0.9 }, "<");
        });
        return tl;
      };

      const drift = () => {
        // slow aurora drift — hardcoded variation per blob (no Math.random: resume-safe)
        gsap.to(".story-hero-blob-a", { xPercent: 12, yPercent: 10, scale: 1.15, duration: 11, repeat: -1, yoyo: true, ease: "sine.inOut" });
        gsap.to(".story-hero-blob-b", { xPercent: -10, yPercent: -12, scale: 1.1, duration: 14, repeat: -1, yoyo: true, ease: "sine.inOut" });
        gsap.to(".story-hero-blob-c", { xPercent: 8, yPercent: -8, scale: 1.2, duration: 9, repeat: -1, yoyo: true, ease: "sine.inOut" });
        // sonar rings pulsing outward behind the headline
        gsap.utils.toArray<HTMLElement>(".story-hero-ring").forEach((ring, i) => {
          gsap.fromTo(
            ring,
            { scale: 0.55, opacity: 0.35 },
            { scale: 1.45, opacity: 0, duration: 5, repeat: -1, ease: "power1.out", delay: i * 2.5 },
          );
        });
      };

      const mm = gsap.matchMedia();

      mm.add(MM_CONDITIONS.desktop, () => {
        const split = SplitText.create(".story-hero-headline", { type: "chars" });
        // opacity floor (not autoAlpha): chars must register their paint at
        // mount, or the staggered reveal emits a later LCP candidate.
        gsap.from(split.chars, {
          yPercent: 60,
          opacity: HIDDEN,
          stagger: 0.03,
          duration: 0.8,
          ease: "power3.out",
        });
        buildLoop(true);
        drift();
        if (cue) gsap.to(cue, { y: 8, repeat: -1, yoyo: true, duration: 0.9, ease: "sine.inOut" });
        return () => split.revert();
      });

      mm.add(MM_CONDITIONS.mobile, () => {
        // Animate position only (NOT opacity): the headline must paint at full
        // opacity on first frame so it becomes the LCP element early. Opacity-
        // animated elements register LCP late, letting the perpetually-cycling
        // flip-sub (opacity:0.001) win LCP at ~7s instead.
        gsap.from(".story-hero-headline", { y: 24, duration: 0.7, ease: "power2.out" });
        buildLoop(true);
        drift();
        if (cue) gsap.to(cue, { y: 8, repeat: -1, yoyo: true, duration: 0.9, ease: "sine.inOut" });
      });

      // Reduced motion: gentle opacity-only crossfade so all three pillars remain discoverable
      mm.add(MM_CONDITIONS.reduceMotion, () => {
        buildLoop(false);
      });
    },
    { scope },
  );

  return (
    <section
      ref={scope}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background"
    >
      {/* Living background: grid + aurora blobs + sonar rings */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148,163,184,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 75% 65% at 50% 42%, black 25%, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse 75% 65% at 50% 42%, black 25%, transparent 78%)",
          }}
        />
        <div className="story-hero-blob-a story-hero-glow absolute -top-[15%] left-[8%] w-[55vw] h-[55vw] max-w-[760px] max-h-[760px] rounded-full blur-[120px]" />
        <div className="story-hero-blob-b story-hero-glow-soft absolute top-[30%] right-[-12%] w-[45vw] h-[45vw] max-w-[620px] max-h-[620px] rounded-full blur-[110px]" />
        <div className="story-hero-blob-c absolute bottom-[-18%] left-[28%] w-[40vw] h-[40vw] max-w-[540px] max-h-[540px] rounded-full blur-[110px] bg-[rgba(6,182,212,0.07)]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="story-hero-ring absolute w-[720px] h-[720px] rounded-full border border-white/10" />
          <div className="story-hero-ring absolute w-[720px] h-[720px] rounded-full border border-white/10" />
        </div>
        {/* fade into the next section */}
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-b from-transparent to-[#0D1626]" />
      </div>

      <div className="container relative z-10 flex flex-col items-center text-center px-4">
        <h1 className="story-hero-headline text-4xl sm:text-6xl lg:text-7xl font-bold font-display tracking-tight">
          {t("rb.story.hero.headline")}
        </h1>

        {/* Flip stage — all three pillars are real DOM text; GSAP only positions them */}
        <div className="mt-6 md:mt-8 w-full" style={{ perspective: "900px" }}>
          <div className="relative h-[2.5em] sm:h-[1.35em] text-4xl sm:text-6xl lg:text-7xl font-bold font-display tracking-tight">
            {FLIP.map(({ key, icon: Icon, accent }) => {
              const a = ACCENT[accent];
              return (
                <div
                  key={key}
                  className="story-flip-word absolute inset-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:gap-x-4"
                >
                  <Icon className={`w-8 h-8 sm:w-12 sm:h-12 ${a.text} shrink-0`} />
                  <span className={a.text}>{t(`rb.story.hero.pillars.${key}.name`)}</span>
                  {key === "agent" ? (
                    <span className="basis-full sm:basis-auto flex justify-center sm:self-start sm:mt-2 sm:ml-1">
                      <span className="text-xs sm:text-sm font-semibold tracking-wider whitespace-nowrap text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded-full px-3 py-1 sm:px-4 sm:py-1.5">
                        {t("rb.story.hero.pillars.agent.badge")}
                      </span>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="relative mt-5 h-[3.2em] sm:h-[2.6em] text-lg sm:text-xl text-muted-foreground">
            {FLIP.map(({ key }) => (
              <p key={key} className="story-flip-sub absolute inset-x-0 top-0 mx-auto max-w-xl leading-relaxed">
                {t(`rb.story.hero.pillars.${key}.sub`)}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="story-scroll-cue absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-muted-foreground">
        <span className="text-xs uppercase tracking-[0.2em]">{t("rb.story.hero.scrollCue")}</span>
        <ChevronDown className="w-5 h-5" />
      </div>
    </section>
  );
}
